import { get, post } from '../../lib/utils/axiosFetcher'; // dap.api
import * as axiosSiteApi from '../../lib/utils/axiosSiteApi';
import { logger } from '../../logger';
// import mockData, { smaGlobalSetting, mockProtocolRes } from './mockData';
import {
  IInitialFilterRes,
  IGetAvailableSitesParams,
  IGetAvailableSitesResponse,
  IProtocolListRes,
} from './types';
import { ISiteMngData } from './types';
import axios, { AxiosResponse } from 'axios';

export enum Roles {
  AUTHOR = 'SMAAuthor',
  APPROVER = 'SMAApprover',
  ADMIN = 'Admin',
}

/** True when the value is a non-empty string that includes any whitespace (leading, trailing, or internal). */
export const siteIdentifierContainsWhitespace = (value: unknown): boolean => {
  if (typeof value !== 'string' || value === '') {
    return false;
  }
  return /\s/.test(value);
};

/** User-facing validation for Site ID (ctmsRowId) and Site Code before save. */
export const getSiteIdentifierWhitespaceValidationMessage = (
  ctmsRowId: unknown,
  siteCode: unknown
): string | null => {
  if (siteIdentifierContainsWhitespace(ctmsRowId)) {
    return 'Site ID cannot contain any spaces before or after the value. Remove all spaces and try again.';
  }
  if (siteIdentifierContainsWhitespace(siteCode)) {
    return 'Site Code cannot contain any spaces before or after the value. Remove all spaces and try again.';
  }
  return null;
};

export const formatSitesForTranslation = (
  sites: ISiteMngData[],
  toLang: string,
  fieldOptions: any
): any => {
  const getTranslateableFields = (fieldOptions: any) => {
    const translateableFields: string[] = [];
    for (const fields in fieldOptions) {
      if (fieldOptions[fields]?.isTranslatable) {
        // TODO change to field value for translation from isEditable
        translateableFields.push(fieldOptions[fields]?.fieldKey);
      }
    }
    return translateableFields;
  };

  const sitesDataRequest: any = [];
  sites.map((site: ISiteMngData) => {
    const translateableFields = getTranslateableFields(fieldOptions);
    const formattedObj: any = {};

    for (const [key, value] of Object.entries(site)) {
      if (translateableFields.includes(key)) {
        formattedObj[key] = value;
      }
    }
    formattedObj.id = site.uid;
    sitesDataRequest.push(formattedObj);
  });

  const formattedSites = {
    from: 'en', //TODO cross verify with deependra
    to: toLang,
    sitesDataRequest: sitesDataRequest,
  };

  return formattedSites;
};

function isBlankResponse(data: unknown): boolean {
  if (data == null) return true;
  if (typeof data === 'string') return data.trim() === '';
  if (Array.isArray(data)) return data.length === 0;
  return false;
}

function extractErrorMessageFromResponse(data: unknown): string {
  if (data == null) return '';
  if (typeof data === 'string') return data.trim();
  if (typeof data === 'object' && !Array.isArray(data)) {
    const o = data as Record<string, unknown>;
    const msg = o.message ?? o.Message ?? o.error ?? o.title ?? o.detail;
    if (typeof msg === 'string' && msg.trim()) return msg.trim();
    try {
      return JSON.stringify(data);
    } catch {
      return 'Request failed';
    }
  }
  return String(data);
}

export const fetchSites = async (
  protocolName: string,
  language: string,
  siteDetailTarget: string
): Promise<any[]> => {
  try {
    const reqBody = {
      protocolName,
      language,
      siteDetailTarget,
    };
    if (!reqBody.protocolName) {
      alert('Protocol is required');
    }
    if (!reqBody.language) {
      alert('Language is required');
    }
    // return mockData;
    const response = await post('/SMA/FetchSMASitesDataFromDb', reqBody);
    const responseData = response.data;
    if (Array.isArray(responseData) && responseData.length > 0) {
      return responseData;
    } else {
      return [];
    }
  } catch (_error) {
    return [];
  }
};

export interface ISaveSitesOptions {
  saveAndPublish?: boolean;
  /** When true, return API protocolItemId (publish / save-and-publish flows). */
  returnProtocolItemId?: boolean;
}

export const saveSites = async (
  sites: ISiteMngData[],
  options?: ISaveSitesOptions
): Promise<string> => {
  try {
    const body = { sites, isSavePublish: options?.saveAndPublish };
    const response = await post('/SMA/SaveSitesDataInDbFromSma', body);
    const status = response.status;
    const data = response.data;
    if (status === 200 || status === 201) {
      if (options?.returnProtocolItemId || options?.saveAndPublish) {
        return typeof data === 'string' ? data.trim() : '';
      }
      return 'saved';
    }
    if (!isBlankResponse(data)) {
      logger.error('SaveSitesDataInDbFromSma failed', {
        status,
        errorMessage: extractErrorMessageFromResponse(data),
      });
    } else {
      logger.error('SaveSitesDataInDbFromSma failed', { status });
    }
    return '';
  } catch (error: any) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    if (status != null && status !== 200 && status !== 201 && !isBlankResponse(data)) {
      logger.error('SaveSitesDataInDbFromSma failed', {
        status,
        errorMessage: extractErrorMessageFromResponse(data),
      });
    } else if (status != null && status !== 200 && status !== 201) {
      logger.error('SaveSitesDataInDbFromSma failed', { status });
    } else {
      logger.error('SaveSitesDataInDbFromSma request error', { error });
    }
    return '';
  }
};

/** Matches DAP API `PublishMutationRequest` (PascalCase for .NET binding). */
export interface IPublishSiteDetailsRequest {
  EprId?: string | null;
  ProtocolItemId?: string | null;
  Language?: string | null;
}

/**
 * Triggers `/SMA/PublishSiteDetails` without awaiting or inspecting the response (fire-and-forget).
 */
export function publishSiteDetails(request: IPublishSiteDetailsRequest): void {
  const body = {
    EprId: request.EprId ?? null,
    ProtocolItemId: request.ProtocolItemId ?? null,
    Language: request.Language ?? null,
  };
  void post('/SMA/PublishSiteDetails', body).catch((error) => {
    logger.error('PublishSiteDetails async request failed', { error });
  });
}

export const translateSites = async (translationData: any): Promise<any> => {
  try {
    const reqBody = translationData;
    const response = await post('/SMA/TranslationSMASitesData', reqBody);
    const responseData = response.data;
    return responseData;
  } catch (_error) {
    return false;
  }
};

export const getSMAGlobalSetting = async (): Promise<IInitialFilterRes> => {
  try {
    // return smaGlobalSetting;
    const response = await axiosSiteApi.get('/SMA/GetSMAGlobalSetting');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const isSiteIdAlreadyAvailable = async (id: any): Promise<any> => {
  try {
    const response = await post(`/SMA/CheckExistingSitecode?siteCode=${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getSMASiteSetting = async (eprId: string, lang: string): Promise<IProtocolListRes> => {
  try {
    const response = await axiosSiteApi.get(
      `/SMA/GetSMASiteSetting?eprId=${encodeURIComponent(eprId)}&language=${encodeURIComponent(
        lang
      )}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getProtocolList = async (
  eprId: string,
  lang: string,
  isGlobal: boolean
): Promise<IProtocolListRes> => {
  try {
    // return mockProtocolRes;
    if (isGlobal) {
      const globalId = '{01442196-D652-4F46-997F-8A741C1D5FDC}';
      const response = await axiosSiteApi.get(
        `/SMA/GetGlobalProtocolItems?globalItemId=${globalId}&language=${lang}`
      );
      return { protocol: response.data };
    }
    return getSMASiteSetting(eprId, lang);
  } catch (error) {
    throw error;
  }
};

export interface IEprSiteContext {
  siteSettingsItemId: string;
  countryItemId: string;
  countryName: string;
  languageCode: string;
  countryCode: string;
  dataFolderItemId: string;
}

export const getEprSiteContext = async (
  eprId: string,
  language: string
): Promise<IEprSiteContext> => {
  try {
    const response = await axiosSiteApi.get(
      `/SMA/GetEprSiteContext?eprId=${encodeURIComponent(eprId)}&language=${encodeURIComponent(
        language
      )}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export interface IStateApiItem {
  stateItemID: string;
  itemName: string;
  itemPath: string;
  key: string;
  value: string;
  label: string;
  isTerritoryState: string;
  cc_TerritoryState: string;
}

export const getCountryStates = async (
  countryItemId: string,
  language: string
): Promise<IStateApiItem[]> => {
  try {
    const response = await axiosSiteApi.get(
      `/Sitecore/GetCountryStates?countryItemId=${encodeURIComponent(
        countryItemId
      )}&language=${encodeURIComponent(language)}`
    );
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    throw error;
  }
};

const buildGetSitesQueryString = (params: Record<string, string>): string =>
  Object.entries(params)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');

export const validateAvailableSitesForm = (params: {
  epr: string;
  language: string;
  protocols: string[];
  stateItemId: string;
  siteContext: IEprSiteContext | null;
  locationMode: 'zipcode' | 'coordinates';
  zipcode: string;
  latitude: string;
  longitude: string;
}): string[] => {
  const errors: string[] = [];

  if (!params.epr) {
    errors.push('EPR is required.');
  }
  if (!params.language) {
    errors.push('Language is required.');
  }
  if (!params.protocols.length) {
    errors.push('At least one eligible protocol is required.');
  }
  if (!params.stateItemId) {
    errors.push('State is required.');
  }
  if (!params.siteContext?.siteSettingsItemId || !params.siteContext?.dataFolderItemId) {
    errors.push('Site context is not loaded. Select EPR and language and try again.');
  }
  if (params.locationMode === 'zipcode') {
    if (!params.zipcode.trim()) {
      errors.push('ZIP Code is required.');
    }
  } else if (!params.latitude.trim() || !params.longitude.trim()) {
    errors.push('Latitude and longitude are required.');
  } else if (Number.isNaN(Number(params.latitude)) || Number.isNaN(Number(params.longitude))) {
    errors.push('Latitude and longitude must be valid numbers.');
  }

  return errors;
};

export const getAvailableSites = async (
  params: IGetAvailableSitesParams
): Promise<IGetAvailableSitesResponse> => {
  const queryParams: Record<string, string> = {
    EligibleProtocols: `${params.eligibleProtocols.join(',')},`,
    SiteRootPath: params.siteRootPath,
    Language: params.language,
    InvitedProtocol: '',
    InvitedSite: '',
    Latitude: params.latitude ?? '',
    Longitude: params.longitude ?? '',
    PatientAddress: '',
    PatientCity: '',
    PatientState: params.patientState,
    ZipCode: params.zipCode ?? '',
    SiteSettingsId: params.siteSettingsId,
    SiteCount: String(params.siteCount ?? 5),
    isSitesRequired: 'true',
    EthnicityValue: '',
    protocolSettingsId: params.protocolSettingsId,
  };

  const response = await get(`/SiteSelection/GetSites?${buildGetSitesQueryString(queryParams)}`);
  return response.data;
};

/**
 * Adds uid to every site both parent and subRows
 *  uid is just a combination of smaRowId + _ + source
 */
function addUIDToSitesData(data: ISiteMngData[]) {
  const newData = data.map((item) => {
    const uid = `${item.smaRowId}_${item.source}`;
    return { ...item, uid };
  });

  newData.forEach((item) => {
    if (item.subRows && Array.isArray(item.subRows) && item.subRows.length > 0) {
      item.subRows.forEach((subItem) => {
        const subUID = `${subItem.smaRowId}_${subItem.source}`;
        subItem.uid = subUID;
      });
    }
  });

  return newData;
}

/**
 * Sorts the fetched sites data based on the orderMap so that staging is always showed on the top
 */
function customSort(arr: ISiteMngData[]) {
  const orderMap: Record<string, number> = {
    staging: 0,
    source: 1,
    published: 2,
  };

  arr.sort((a, b) => {
    const sourceA = a.source;
    const sourceB = b.source;

    const orderA = orderMap[sourceA] !== undefined ? orderMap[sourceA] : Infinity;
    const orderB = orderMap[sourceB] !== undefined ? orderMap[sourceB] : Infinity;

    return orderA - orderB;
  });

  return arr;
}

export const getSites = async (
  protocolName: string,
  language: string,
  siteDetailTarget: string
): Promise<any[]> => {
  const sitesFromDB = await fetchSites(protocolName, language, siteDetailTarget);
  const sitesWithUid = addUIDToSitesData(sitesFromDB);
  const sitessorted = customSort(sitesWithUid);
  // sitessorted.forEach((item) => {
  //   item.approve = false;
  // });
  return sitessorted;
};

export async function verifyJwtToken(jwtToken: string): Promise<any> {
  try {
    const apiUrl = '/api/verify-sma-token';
    const response: AxiosResponse<any> = await axios.post(apiUrl, {
      token: jwtToken,
    });
    return response.data;
  } catch (error) {
    return { isValid: false, payload: {} };
  }
}

export const saveCTMSSitesToRawDb = async (
  protocolName: string,
  globalSettings: any = {},
  statusList: string // Comma separated value of all site status options
) => {
  const {
    optedIn = false,
    radius = 0,
    //default priority is blank
    priority = '',
    threshold = 0,
    warmTransferNumber = '',
    optInDateTime = new Date().toISOString(),
    isDefaultVirtualSite = false,
    isHybridSite = false,
    kiElementsEnabled = false,
    siteDirectedSchedule = false,
    language = 'en',
  } = globalSettings;
  const reqBody = {
    protocols: protocolName,
    siteStatuses: statusList,
    updatedBy: 'CTMS',
    config: {
      isDefaultVirtualSite,
      isHybridSite,
      kiElementsEnabled,
      optedIn,
      optInDateTime,
      priority,
      radius,
      siteDirectedSchedule,
      threshold,
      warmTransferNumber,
      language,
    },
  };

  try {
    await post('/CTMS/SaveCTMSSitesToRawDb', reqBody);
  } catch (error) {
    throw error;
  }
};

export const getCountryStatesList = async (lang: string): Promise<IProtocolListRes> => {
  try {
    const countryItemId = '{0BAEDE80-BEE8-493D-B548-B60A6935A31C}';
    const response = await axiosSiteApi.get(
      `/SMA/GetGlobalStateData?globalCountryItemId=${countryItemId}&language=${lang}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};
